// Locale registry for the EU localization subsystem. The locale LIST + prefixes/hreflang come from
// data.mjs (single source, shared with prerender); this module attaches the inline flag SVGs (JSX,
// so app-only) and exposes typed helpers. English lives at the root; every other locale is a
// subdirectory so all authority stays on one Cloudflare Worker.
import type { ReactNode } from 'react'
import { DEFAULT_LOCALE as DEFAULT, GATEWAYS, LOCALES as RAW } from './data.mjs'
import { FLAGS, flagFallback } from './flags'

export type Locale = string

export interface LocaleMeta {
  code: Locale
  htmlLang: string
  hreflang: string
  prefix: string
  label: string
  /** Inline flag (CSP blocks external images). */
  flag: ReactNode
}

export const DEFAULT_LOCALE: Locale = DEFAULT

export const LOCALES: LocaleMeta[] = RAW.map((l) => ({ ...l, flag: FLAGS[l.code] ?? flagFallback(l.code) }))

export const localeMeta = (code: Locale): LocaleMeta =>
  LOCALES.find((l) => l.code === code) ?? (LOCALES[0] as LocaleMeta)

/** Derive the active locale from a pathname (e.g. '/de/rates/x' → 'de'). */
export function localeFromPath(pathname: string): Locale {
  for (const l of LOCALES) {
    if (l.prefix && (pathname === l.prefix || pathname.startsWith(`${l.prefix}/`))) return l.code
  }
  return DEFAULT_LOCALE
}

/** Strip the locale prefix from a pathname → the logical (English) path. Always starts with '/'. */
export function stripLocale(pathname: string): string {
  const code = localeFromPath(pathname)
  const { prefix } = localeMeta(code)
  if (!prefix) return pathname || '/'
  const rest = pathname.slice(prefix.length)
  return rest.startsWith('/') ? rest : `/${rest}` || '/'
}

/** Prefix a logical path for a locale. localePath('de', '/rates/x') → '/de/rates/x'; '/' → '/de'. */
export function localePath(code: Locale, path: string): string {
  const { prefix } = localeMeta(code)
  if (!prefix) return path || '/'
  if (path === '/' || path === '') return prefix
  return `${prefix}${path.startsWith('/') ? path : `/${path}`}`
}

// Logical paths that exist in only some locales — each locale's country money-page slug belongs only
// to that locale. Used so the language switcher never links to a non-existent URL.
const LOCALE_EXCLUSIVE: Record<string, Locale[]> = Object.fromEntries(
  Object.entries(GATEWAYS).map(([code, g]) => [`/${g.slug}`, [code]])
)

/** Best URL for `target` locale given the current pathname — falls back to the target's home when the
 *  current page has no counterpart there (so the switcher/nudge can never produce a dead link). */
export function switchPath(target: Locale, pathname: string): string {
  const logical = stripLocale(pathname)
  const only = LOCALE_EXCLUSIVE[logical]
  if (only && !only.includes(target)) return localePath(target, '/')
  return localePath(target, logical)
}

export { GATEWAYS } from './data.mjs'
